import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Image from '../../../components/AppImage';

const PersonalInfoSection = ({ user, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    dateOfBirth: user?.dateOfBirth || '',
    gender: user?.gender || '',
    location: user?.location || '',
    emergencyContact: user?.emergencyContact || '',
    emergencyPhone: user?.emergencyPhone || ''
  });

  const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' },
    { value: 'prefer-not-to-say', label: 'Prefer not to say' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    onSave(formData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData({
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      dateOfBirth: user?.dateOfBirth || '',
      gender: user?.gender || '',
      location: user?.location || '',
      emergencyContact: user?.emergencyContact || '',
      emergencyPhone: user?.emergencyPhone || ''
    });
    setIsEditing(false);
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Icon name="User" size={24} className="text-primary" />
          <h2 className="text-xl font-heading font-semibold text-foreground">Personal Information</h2>
        </div>
        {!isEditing ? (
          <Button
            variant="outline"
            onClick={() => setIsEditing(true)}
            iconName="Edit"
            iconPosition="left"
          >
            Edit Profile
          </Button>
        ) : (
          <div className="flex space-x-2">
            <Button
              variant="outline"
              onClick={handleCancel}
              iconName="X"
              iconPosition="left"
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleSave}
              iconName="Check"
              iconPosition="left"
            >
              Save Changes
            </Button>
          </div>
        )}
      </div>
      {/* Profile Picture */}
      <div className="flex items-center space-x-6 mb-8">
        <div className="relative">
          <div className="w-24 h-24 rounded-full overflow-hidden bg-muted">
            <Image
              src={user?.avatar || "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face"}
              alt="Profile Picture"
              className="w-full h-full object-cover"
            />
          </div>
          {isEditing && (
            <button className="absolute -bottom-2 -right-2 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center shadow-wellness">
              <Icon name="Camera" size={16} />
            </button>
          )}
        </div>
        <div>
          <h3 className="text-lg font-heading font-medium text-foreground">{user?.name}</h3>
          <p className="text-muted-foreground">{user?.email}</p>
          <div className="flex items-center space-x-2 mt-2">
            <div className="w-2 h-2 bg-accent rounded-full animate-breathing"></div>
            <span className="text-sm font-caption text-accent-foreground font-medium">
              {user?.tier} Member
            </span>
          </div>
        </div>
      </div>
      {/* Form Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Full Name"
          type="text"
          value={formData?.name}
          onChange={(e) => handleInputChange('name', e?.target?.value)}
          disabled={!isEditing}
          required
        />

        <Input
          label="Email Address"
          type="email"
          value={formData?.email}
          onChange={(e) => handleInputChange('email', e?.target?.value)}
          disabled={!isEditing}
          required
        />

        <Input
          label="Phone Number"
          type="tel"
          value={formData?.phone}
          onChange={(e) => handleInputChange('phone', e?.target?.value)}
          disabled={!isEditing}
          placeholder="+1 (555) 123-4567"
        />

        <Input
          label="Date of Birth"
          type="date"
          value={formData?.dateOfBirth}
          onChange={(e) => handleInputChange('dateOfBirth', e?.target?.value)}
          disabled={!isEditing}
        />

        <Select
          label="Gender"
          options={genderOptions}
          value={formData?.gender}
          onChange={(value) => handleInputChange('gender', value)}
          disabled={!isEditing}
          placeholder="Select gender"
        />

        <Input
          label="Location"
          type="text"
          value={formData?.location}
          onChange={(e) => handleInputChange('location', e?.target?.value)}
          disabled={!isEditing}
          placeholder="City, State"
        />

        <Input
          label="Emergency Contact"
          type="text"
          value={formData?.emergencyContact}
          onChange={(e) => handleInputChange('emergencyContact', e?.target?.value)}
          disabled={!isEditing}
          placeholder="Contact person name"
        />

        <Input
          label="Emergency Phone"
          type="tel"
          value={formData?.emergencyPhone}
          onChange={(e) => handleInputChange('emergencyPhone', e?.target?.value)}
          disabled={!isEditing}
          placeholder="+1 (555) 123-4567"
        />
      </div>
      {/* Account Status */}
      <div className="mt-8 pt-6 border-t border-border">
        <h3 className="text-lg font-heading font-medium text-foreground mb-4">Account Status</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center space-x-3 p-4 bg-success/10 rounded-lg">
            <Icon name="CheckCircle" size={20} className="text-success" />
            <div>
              <p className="text-sm font-medium text-foreground">Email Verified</p>
              <p className="text-xs text-muted-foreground">Verified on 09/15/2025</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-4 bg-success/10 rounded-lg">
            <Icon name="Shield" size={20} className="text-success" />
            <div>
              <p className="text-sm font-medium text-foreground">Account Secure</p>
              <p className="text-xs text-muted-foreground">2FA Enabled</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-4 bg-accent/10 rounded-lg">
            <Icon name="Calendar" size={20} className="text-accent" />
            <div>
              <p className="text-sm font-medium text-foreground">Member Since</p>
              <p className="text-xs text-muted-foreground">{user?.joinDate}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalInfoSection;